import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ConfirmationModal, FullColumn, normalizeDescription } from 'seagull';
import ServiceDescription from '~/components/ServiceBuilder/ServiceDescription';
import ServiceNameTextField from '~/components/ServiceBuilder/ServiceNameTextField';
import { useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import HelpMeWriteDrawer from '../ServiceBuilder/HelpMeWriteDrawer/HelpMeWriteDrawer';
import { EditServiceContentModalTestIds } from './EditServiceContentModal.utils';
const EditServiceContentModal = (param)=>{
    let { serviceName, description, serviceId, title, width, dismissModal, onSave, serviceNameBlacklist, bundle, showAiDescriptionPanel: showAiDescriptionPanelProp = false } = param;
    const { SERVICE_DESCRIPTION_AI } = useFeatureFlags();
    const { logBI } = useStoreActions();
    const form = useForm({
        defaultValues: useMemo(()=>({
                serviceName,
                description,
                bundle: bundle === null || bundle === void 0 ? void 0 : bundle.services
            }), [
            serviceName,
            description,
            bundle === null || bundle === void 0 ? void 0 : bundle.services
        ])
    });
    const [isHelpMeWriteDrawerOpen, setIsHelpMeWriteDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(564);
    const calculateDrawerWidth = useCallback(()=>{
        if (!width) {
            setDrawerWidth(564);
            return;
        }
        const modalElement = document.querySelector('[data-testid="'.concat(EditServiceContentModalTestIds.EditServiceModal(), '"]'));
        if (!modalElement) {
            setDrawerWidth(564);
            return;
        }
        const modalRect = modalElement.getBoundingClientRect();
        const translationAmount = 250;
        const modalRightEdgeAfterTranslation = modalRect.right - translationAmount;
        const gap = 32;
        const rightMargin = 32;
        const calculatedWidth = window.innerWidth - modalRightEdgeAfterTranslation - gap - rightMargin;
        setDrawerWidth(Math.min(600, Math.max(300, calculatedWidth)));
    }, [
        width
    ]);
    useEffect(()=>{
        const timeoutId = setTimeout(()=>{
            calculateDrawerWidth();
        }, 0);
        const handleResize = ()=>{
            calculateDrawerWidth();
        };
        window.addEventListener('resize', handleResize);
        return ()=>{
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, [
        width,
        calculateDrawerWidth
    ]);
    const onSubmit = (data)=>{
        onSave({
            name: data.serviceName,
            description: data.description
        });
    };
    const isBundle = Boolean(bundle === null || bundle === void 0 ? void 0 : bundle.services);
    const showAiDescriptionPanel = SERVICE_DESCRIPTION_AI && !isBundle && showAiDescriptionPanelProp;
    const disableAiDescriptionPanel = showAiDescriptionPanel && form.watch('serviceName') === '';
    const onHelpMeWriteClick = showAiDescriptionPanel ? ()=>{
        logBI(17001);
        if (!isHelpMeWriteDrawerOpen) {
            calculateDrawerWidth();
        }
        setIsHelpMeWriteDrawerOpen((prev)=>!prev);
    } : undefined;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                width: width,
                testId: EditServiceContentModalTestIds.EditServiceModal(),
                title: title,
                style: {
                    transform: isHelpMeWriteDrawerOpen ? 'translateX(-250px)' : 'translateX(0)',
                    transition: 'transform 0.2s ease-in-out'
                },
                content: /*#__PURE__*/ _jsx(FormProvider, _object_spread_props(_object_spread({}, form), {
                    children: /*#__PURE__*/ _jsx("form", {
                        onSubmit: form.handleSubmit(onSubmit),
                        children: /*#__PURE__*/ _jsxs(FullColumn, {
                            gap: 24,
                            children: [
                                /*#__PURE__*/ _jsx(ServiceNameTextField, {
                                    testId: EditServiceContentModalTestIds.EditServiceNameInput(),
                                    serviceNameBlacklist: serviceNameBlacklist,
                                    disabled: isHelpMeWriteDrawerOpen
                                }),
                                /*#__PURE__*/ _jsx(ServiceDescription, {
                                    onHelpMeWriteClick: onHelpMeWriteClick,
                                    disabledHelpMeWrite: disableAiDescriptionPanel,
                                    isDrawerOpen: isHelpMeWriteDrawerOpen,
                                    testId: EditServiceContentModalTestIds.EditServiceDescriptionInput(),
                                    disabled: isHelpMeWriteDrawerOpen
                                })
                            ]
                        })
                    })
                })),
                onPrimaryButtonClick: form.handleSubmit(onSubmit),
                onSecondaryButtonClick: ()=>dismissModal(),
                primaryButtonLabel: "Save",
                secondaryButtonLabel: "Cancel",
                primaryButtonTestId: EditServiceContentModalTestIds.EditServiceModalSaveButton()
            }),
            SERVICE_DESCRIPTION_AI && /*#__PURE__*/ _jsx(HelpMeWriteDrawer, {
                open: isHelpMeWriteDrawerOpen,
                onClose: ()=>setIsHelpMeWriteDrawerOpen(false),
                serviceName: serviceName,
                serviceDescription: description,
                serviceId: serviceId,
                drawerWidth: drawerWidth,
                onInsert: (parsedRichText)=>{
                    form.setValue('description', normalizeDescription(parsedRichText), {
                        shouldDirty: true
                    });
                }
            })
        ]
    });
};
export default EditServiceContentModal;
